import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, BookOpen, Globe, ThumbsUp, ThumbsDown, BookmarkPlus,
  Filter, X, RefreshCw, MessageCircle, ChevronDown, ChevronUp, Flag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { sentenceQaApi } from '../utils/api';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 20;

// ── Language display helpers ─────────────────────────────────────────────────
const LANG_NAMES = {
  es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese',
  ja: 'Japanese', ko: 'Korean', zh: 'Chinese', ar: 'Arabic', ru: 'Russian',
  nl: 'Dutch', pl: 'Polish', tr: 'Turkish', sv: 'Swedish', no: 'Norwegian',
  da: 'Danish', fi: 'Finnish', el: 'Greek', he: 'Hebrew', hi: 'Hindi',
  fa: 'Persian', hbo: 'Biblical Hebrew', grc: 'Ancient Greek', la: 'Latin',
  kea: 'Cape Verdean Creole', ht: 'Haitian Creole',
};
const langName = (code) => LANG_NAMES[code] || code?.toUpperCase() || '?';

// ── Explanation card ─────────────────────────────────────────────────────────
function ExplanationCard({ exp, userIsAdmin, onSaved }) {
  const [userVote, setUserVote] = useState(0);
  const [voting, setVoting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportText, setReportText] = useState('');

  const handleVote = async (v) => {
    const next = userVote === v ? (v === 1 ? -1 : 1) : v;
    setVoting(true);
    try {
      await sentenceQaApi.voteExplanation(exp.explanation_id, next);
      setUserVote(userVote === v ? 0 : v);
      toast.success(v === 1 ? 'Marked helpful' : 'Marked not helpful');
    } catch {
      toast.error('Could not record vote');
    } finally {
      setVoting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sentenceQaApi.saveToFlashcard(exp.explanation_id);
      if (res.data?.duplicate) {
        toast.info('Already saved in your "Sentence Explanations" list');
      } else {
        toast.success('Saved to "Sentence Explanations" flashcard list');
        onSaved?.();
      }
    } catch {
      toast.error('Could not save flashcard');
    } finally {
      setSaving(false);
    }
  };

  const handleReport = async () => {
    try {
      await sentenceQaApi.reportExplanation(exp.explanation_id, reportText.trim() || 'No reason provided');
      toast.success('Reported — thank you');
      setShowReport(false);
      setReportText('');
    } catch {
      toast.error('Could not submit report');
    }
  };

  const truncated = exp.explanation_text.length > 280 && !expanded;
  const displayText = truncated ? exp.explanation_text.slice(0, 280) + '…' : exp.explanation_text;

  return (
    <div
      className="bg-card border border-border/50 rounded-xl p-4 flex flex-col gap-3 hover:border-border transition-colors"
      data-testid="library-card"
    >
      {/* Context sentence */}
      {exp.context_sentence && (
        <p className="text-xs text-muted-foreground italic border-l-2 border-primary/30 pl-2 leading-snug line-clamp-2" data-testid="card-context">
          &ldquo;{exp.context_sentence}&rdquo;
        </p>
      )}

      {/* Explanation text */}
      <div>
        <p className="text-sm leading-relaxed text-foreground/90" data-testid="card-explanation">{displayText}</p>
        {exp.explanation_text.length > 280 && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="mt-1 text-xs text-primary/70 hover:text-primary flex items-center gap-0.5"
          >
            {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show more</>}
          </button>
        )}
      </div>

      {/* Topics */}
      {exp.topics?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {exp.topics.map(t => (
            <span key={t.topic_slug} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary/80 border border-primary/20">
              {t.topic_label || t.topic_slug}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-border/30">
        <div className="flex items-center gap-2">
          {/* Language */}
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Globe className="h-3 w-3" />{langName(exp.language_code)}
          </span>
          {/* Score */}
          {(exp.helpfulness_up > 0 || exp.helpfulness_down > 0) && (
            <span className="text-[10px] text-muted-foreground">
              {exp.helpfulness_up}↑ {exp.helpfulness_down}↓
            </span>
          )}
          {/* Revisions */}
          {exp.revision_count > 0 && (
            <span className="text-[10px] text-muted-foreground">rev {exp.revision_count}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleVote(1)}
            disabled={voting}
            className={`p-1.5 rounded-md hover:bg-muted/40 transition-colors ${userVote === 1 ? 'text-green-500' : 'text-muted-foreground'}`}
            title="Helpful"
            data-testid="card-vote-up"
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => handleVote(-1)}
            disabled={voting}
            className={`p-1.5 rounded-md hover:bg-muted/40 transition-colors ${userVote === -1 ? 'text-red-400' : 'text-muted-foreground'}`}
            title="Not helpful"
            data-testid="card-vote-down"
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setShowReport(v => !v)}
            className="p-1.5 rounded-md hover:bg-muted/40 transition-colors text-muted-foreground hover:text-destructive"
            title="Report"
            data-testid="card-report-btn"
          >
            <Flag className="h-3.5 w-3.5" />
          </button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs px-2 gap-1 ml-1"
            onClick={handleSave}
            disabled={saving}
            data-testid="card-save-flashcard"
          >
            {saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <BookmarkPlus className="h-3 w-3" />}
            Save
          </Button>
        </div>
      </div>

      {/* Inline report form */}
      {showReport && (
        <div className="pt-2 border-t border-border/30">
          <Textarea
            value={reportText}
            onChange={e => setReportText(e.target.value)}
            placeholder="Why are you reporting this? (optional)"
            className="resize-none text-xs min-h-[48px]"
            maxLength={300}
            data-testid="card-report-input"
          />
          <div className="flex gap-2 mt-1.5">
            <Button size="sm" variant="destructive" className="text-xs h-7 px-2" onClick={handleReport} data-testid="card-report-submit">Submit</Button>
            <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => setShowReport(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function SentenceLibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);

  // q = live input value; searchTerm = committed value that triggers refetch
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [langFilter, setLangFilter] = useState(searchParams.get('lang') || '');
  const [sortBy, setSortBy] = useState('score');
  const [showFilters, setShowFilters] = useState(false);

  // Available languages from the app catalog
  const [availLangs, setAvailLangs] = useState([]);

  // Check feature flag + load language catalog
  useEffect(() => {
    sentenceQaApi.getStatus()
      .then(r => setEnabled(r.data?.questions !== false))
      .catch(() => setEnabled(true));
    api.get('/languages').then(r => {
      const langs = (Array.isArray(r.data) ? r.data : (r.data?.languages || []))
        .filter(l => l.code)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAvailLangs(langs);
    }).catch(() => {});
  }, []);

  const loadItems = useCallback(async (off = 0, reset = true) => {
    setLoading(true);
    try {
      const res = await sentenceQaApi.getLibrary({
        q: searchTerm,
        language_code: langFilter,
        sort: sortBy,
        offset: off,
        limit: PAGE_SIZE,
      });
      const newItems = res.data?.items || [];
      setTotal(res.data?.total || 0);
      setOffset(off + newItems.length);
      setItems(prev => reset ? newItems : [...prev, ...newItems]);
    } catch (e) {
      toast.error('Could not load explanation library');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, langFilter, sortBy]);

  useEffect(() => {
    loadItems(0, true);
  }, [loadItems]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(q);  // commit the input value → triggers useEffect
  };

  const clearFilters = () => {
    setQ('');
    setSearchTerm('');
    setLangFilter('');
    setSortBy('score');
  };

  const hasFilters = searchTerm || langFilter || sortBy !== 'score';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="sentence-library-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Sentence Explanation Library</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Community-vetted explanations of grammar, vocabulary, and meaning in context.
        </p>
      </div>

      {/* Search + filter bar */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search explanations…"
            className="pl-9 h-9"
            data-testid="library-search-input"
          />
        </div>
        <Button type="submit" size="sm" className="h-9" data-testid="library-search-btn">Search</Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9 gap-1"
          onClick={() => setShowFilters(v => !v)}
          data-testid="library-filter-toggle"
        >
          <Filter className="h-3.5 w-3.5" />
          Filters
          {hasFilters && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary inline-block" />}
        </Button>
      </form>

      {/* Expanded filters */}
      {showFilters && (
        <div className="mb-4 p-3 rounded-lg border border-border/50 bg-card/50 flex flex-wrap gap-3 items-end">
          {/* Language filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-medium">Language</label>
            <select
              value={langFilter}
              onChange={e => setLangFilter(e.target.value)}
              className="h-8 text-sm rounded-md border border-border bg-background px-2 pr-6 focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="library-lang-filter"
            >
              <option value="">All languages</option>
              {availLangs.map(l => (
                <option key={l.code} value={l.code}>{l.name || langName(l.code)}</option>
              ))}
            </select>
          </div>
          {/* Sort */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground font-medium">Sort by</label>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="h-8 text-sm rounded-md border border-border bg-background px-2 pr-6 focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="library-sort-select"
            >
              <option value="score">Most helpful</option>
              <option value="date">Newest</option>
            </select>
          </div>
          {hasFilters && (
            <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={clearFilters} data-testid="library-clear-filters">
              <X className="h-3 w-3" /> Clear
            </Button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-muted-foreground">
          {loading ? 'Loading…' : `${total} explanation${total !== 1 ? 's' : ''}${langFilter ? ` in ${langName(langFilter)}` : ''}`}
        </p>
        {user?.is_admin && (
          <Button
            size="sm" variant="ghost"
            className="text-xs h-7 gap-1"
            onClick={() => navigate('/admin/sentences')}
          >
            <BookOpen className="h-3 w-3" /> Admin
          </Button>
        )}
      </div>

      {/* Loading skeleton */}
      {loading && items.length === 0 && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card border border-border/30 rounded-xl p-4 animate-pulse h-32" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="library-empty">
          <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">No explanations found</p>
          {hasFilters && (
            <p className="text-xs mt-1">
              Try clearing your filters or{' '}
              <button className="underline hover:no-underline" onClick={clearFilters}>reset search</button>.
            </p>
          )}
          {!hasFilters && (
            <p className="text-xs mt-1">
              Explanations appear here once an admin publishes them from the Q&amp;A panel.
            </p>
          )}
        </div>
      )}

      {/* Cards */}
      <div className="space-y-3">
        {items.map(exp => (
          <ExplanationCard
            key={exp.explanation_id}
            exp={exp}
            userIsAdmin={user?.is_admin}
            onSaved={() => {}}
          />
        ))}
      </div>

      {/* Load more */}
      {items.length < total && (
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadItems(offset, false)}
            disabled={loading}
            data-testid="library-load-more"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            Load more ({total - items.length} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
